// src/components/Header.jsx
import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faHome, faComments, faTimes, faArrowRightFromBracket, faChartLine, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '../contexts/UserContext';
import logo from '../assets/RWM Logo-270x270.png'; // Import the logo
import Profile from '../pages/Profile';
import ChatBot from '../components/Chatbot'; // Import the ChatBot component


function Header() {
  const location = useLocation();
  const userContext = useUser();
  const [showProfile, setShowProfile] = useState(false);
  const [showChatBot, setShowChatBot] = useState(false); // State for ChatBot visibility
  const isLandingPage = location.pathname === '/';
  const navigate = useNavigate();
  const firstName = userContext?.user?.first_name || userContext?.user?.firstname;
  const isAuthenticated = Boolean(userContext?.isLoggedIn);

  const handleLogout = async () => {
    userContext.logout(true);
    navigate('/');
  };

  // Close modals if user logs out
  useEffect(() => {
      if (!isAuthenticated) {
          setShowProfile(false);
          setShowChatBot(false);
      }
  }, [isAuthenticated]);
  return (
    <>
      <header id="header" className="bg-primary text-white p-2 h-16 sticky top-0 z-30"> {/* Lowered z-index */}
      <div className="container mx-auto flex items-center justify-between">
        {/* Left Side */}
        <div className="flex items-center gap-4 min-w-0">
          <div className=" h-12 w-auto flex-shrink-0">
            <img
              className="h-12 w-auto"
              src={logo}
              alt="RediMD Weight Management Logo"
            />
          </div>
          <Link
            to="/"
            className="hidden md:block text-lg font-bold cursor-pointer whitespace-nowrap flex-shrink-0"
            title="Go to Homepage"
          >
            RediMD Weight Management
          </Link>
          {!isLandingPage && location.pathname !== '/' &&(
            <Link
              to="/"
              className="text-white hover:bg-primary-light p-2 rounded-full flex items-center gap-1 text-sm"
              aria-label="Back to Home"
              title="Home"
            >
              <FontAwesomeIcon icon={faHome} className="h-4 w-4" />
              <span className="hidden md:inline">Home</span>
            </Link>
          )}
          {isAuthenticated && location.pathname !== '/dashboard' && (
            <Link
              to="/dashboard"
              className="text-white hover:bg-primary-light p-2 rounded-full flex items-center gap-1 text-sm"
              aria-label="Dashboard"
              title="Dashboard"
            >
              <FontAwesomeIcon icon={faChartLine} className="h-4 w-4" />
              <span className="hidden md:inline">Dashboard</span>
            </Link>
          )}
        </div>

        {/* Center */}
        <div className="hidden lg:block flex-1 justify-center min-w-0 max-w-[200px]">
          {firstName && isAuthenticated && (
            <div className="text-white text-sm text-center">
              Welcome {firstName}
            </div>
          )}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4 min-w-0 justify-end">
          {location.pathname !== '/pricing' && (
            <Link
              to="/pricing"
              className="text-white flex items-center gap-1 text-sm hover:bg-primary-light p-2 rounded-full"
              aria-label="View Pricing"
              title="View Pricing"
            >
              <FontAwesomeIcon icon={faDollarSign} className="h-4 w-4" />
              <span className="hidden md:inline">Pricing</span>
            </Link>
          )}
          {!isAuthenticated ? (
            <>
              {location.pathname !== '/login' && (
                  <Link to="/login" className="text-white hover:underline text-sm">
                    Sign In
                  </Link>
              )}
              {location.pathname !== '/register' && (
                  <Link to="/register" className="text-white hover:underline text-sm">
                    Register
                  </Link>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => setShowChatBot(!showChatBot)}
                className="text-white flex items-center gap-1 text-sm hover:bg-primary-light p-2 rounded-full"
                aria-label={showChatBot ? "Close Chat" : "Open Chat"}
                title={showChatBot ? "Close Chat" : "Open Chat"}
              >
                <FontAwesomeIcon icon={showChatBot ? faTimes : faComments} className="h-5 w-5" />
                <span className="hidden md:inline">Ask Amy</span>
              </button>
              <button
                onClick={() => setShowProfile(true)}
                className="text-white flex items-center gap-1 text-sm hover:bg-primary-light p-2 rounded-full"
                aria-label="View Profile"
                title="View Profile"
              >
                <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                <span className="hidden md:inline">Profile</span>
              </button>
              <button
                onClick={handleLogout}
                className="text-white flex items-center gap-1 text-sm hover:bg-primary-light p-2 rounded-full"
                aria-label="Logout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} className="h-4 w-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </>
          )}
        </div>
      </div>
      </header>

      {/* Profile Modal */}
      {showProfile && ( // Ensure user exists before showing profile
        // Removed fixed positioning, Profile component handles it
         <Profile onClose={() => setShowProfile(false)} />
      )}

      {/* ChatBot Component - Rendered conditionally based on user */}
      {showChatBot && userContext?.user && (
          <ChatBot
              isVisible={showChatBot}
              onClose={() => setShowChatBot(false)}
              user={userContext?.user}
          />
      )}
    </>
  );
}

export default Header;
